// src/utils/time/index.ts
// The one timezone conversion module for the frontend (ClickUp 86d42y068 S6).
// Backend timestamps are UTC: convert out of UTC to render, into UTC to save.
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Never extended anywhere before S6, so dayjs had no timezone capability in this app at all.
dayjs.extend(utc);
dayjs.extend(timezone);

// Last-resort zone: the clinic default the healthcare backend seeds.
export const FALLBACK_TIMEZONE = "Australia/Melbourne";

export type TimeInput = string | number | Date | Dayjs | null | undefined;

// A trailing Z or ±HH:MM already names an instant, so it must not be re-read as wall clock.
// Appending "Z" to one of these is what made DateTimeUtils.parseUTCToLocal return Invalid Date.
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

// True when this runtime's ICU data actually knows the IANA name.
export function isValidZone(zone: string | null | undefined): zone is string {
	if (!zone) return false;
	try {
		new Intl.DateTimeFormat(undefined, { timeZone: zone });
		return true;
	} catch {
		return false;
	}
}

// The browser's own zone.
export function deviceZone(): string {
	try {
		const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return isValidZone(resolved) ? resolved : FALLBACK_TIMEZONE;
	} catch {
		return FALLBACK_TIMEZONE;
	}
}

// The zone to display in. `userTimezone` is the profile column landing in S4/S5; omit it until then.
export function viewerZone(userTimezone?: string | null): string {
	return isValidZone(userTimezone) ? userTimezone : deviceZone();
}

let configuredClinicZone: string | null = null;

// A plain module cannot run a query, so whoever holds the settings response publishes the zone here.
export function setClinicZone(zone: string | null | undefined): void {
	configuredClinicZone = isValidZone(zone) ? zone : null;
}

// The clinic's business zone (company_appointment_settings.timezone) — the zone slots are generated in.
// ⚠️ Returns FALLBACK_TIMEZONE until something calls setClinicZone(); nothing does yet.
export function clinicZone(): string {
	return configuredClinicZone ?? FALLBACK_TIMEZONE;
}

function resolveZone(zone?: string | null): string {
	return isValidZone(zone) ? zone : viewerZone();
}

// Value -> the UTC ISO string the backend stores; null when unparseable.
// A bare string is wall clock in `zone`; anything offset-bearing already names an instant.
export function toUtcIso(local: TimeInput, zone?: string | null): string | null {
	if (local === null || local === undefined || local === "") return null;

	if (typeof local === "string") {
		const trimmed = local.trim();
		if (HAS_OFFSET.test(trimmed)) {
			const instant = dayjs(trimmed);
			return instant.isValid() ? instant.toISOString() : null;
		}
		// dayjs.tz() throws RangeError on an unparseable string rather than returning invalid.
		if (!dayjs.utc(trimmed).isValid()) return null;
		return dayjs.tz(trimmed, resolveZone(zone)).toISOString();
	}

	const instant = dayjs(local);
	return instant.isValid() ? instant.toISOString() : null;
}

// Stored UTC value -> a Dayjs in `zone`; null when unparseable.
// A bare string is read as UTC, which is the backend's serialisation convention.
export function fromUtc(iso: TimeInput, zone?: string | null): Dayjs | null {
	if (iso === null || iso === undefined || iso === "") return null;

	const instant =
		typeof iso === "string" && !HAS_OFFSET.test(iso.trim())
			? dayjs.utc(iso.trim())
			: dayjs(iso as Exclude<TimeInput, null | undefined>);

	return instant.isValid() ? instant.tz(resolveZone(zone)) : null;
}

// fromUtc() plus a dayjs format string; empty string when unparseable.
export function formatInZone(iso: TimeInput, fmt: string, zone?: string | null): string {
	return fromUtc(iso, zone)?.format(fmt) ?? "";
}

// The offset in force AT THAT INSTANT, e.g. "UTC+11:00" — DateTimeUtils.getTimezoneOffset() used today's.
export function offsetLabel(iso: TimeInput, zone?: string | null): string {
	const instant = fromUtc(iso, zone);
	return instant ? `UTC${instant.format("Z")}` : "";
}

// The dayjs singleton with utc + timezone already extended, for antd pickers that want a Dayjs.
export { dayjs };
